import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { getSsoConfig, getSsoSession, redirectToSsoLogin } from 'auth/ssoClient'
import { useBrand } from 'config/brand'

/** Long enough to read the line, short enough not to feel like a stop. */
const REDIRECT_AFTER_MS = 1200

/**
 * There is no sign-in form here. The app is entered from the main Kit19 product,
 * which puts the SSO token in the link, so this page is only ever reached when a
 * visit carries no token and no stored session.
 *
 * It used to render a bare spinner and redirect at once, which looked like a
 * blank screen whenever the hop to SSO was slow or blocked. Now it says why it is
 * sending you away, then goes — with a button in case the redirect never lands.
 */
export default function Login() {
  useBrand('Sign in')
  const location = useLocation()
  const sso = getSsoConfig()
  const state = location.state as { from?: string; error?: string } | null
  const target = state?.from
  const ssoError = state?.error
  const [redirecting, setRedirecting] = useState(false)
  const navigate = useNavigate()
  const signedIn = getSsoSession().isAuthenticated

  useEffect(() => {
    if (!sso.enabled || ssoError) return undefined
    if (signedIn) {
      // a session is already in hand (SSO sent us back here, or the page was
      // bookmarked): go where we were headed instead of round-tripping again
      const to = target && target.startsWith('/') && !target.startsWith('/login') ? target : '/'
      navigate(to, { replace: true })
      return undefined
    }
    const timer = window.setTimeout(() => {
      setRedirecting(true)
      redirectToSsoLogin(target)
    }, REDIRECT_AFTER_MS)
    return () => window.clearTimeout(timer)
  }, [sso.enabled, ssoError, target, signedIn, navigate])

  if (!sso.enabled) {
    return (
      <div className="app-shell">
        <div className="auth-error card">
          <h2>Sign-in is switched off</h2>
          <p>
            <code>REACT_APP_SSO_ENABLED=false</code> in this build. Open the record from the CRM (View / Edit entity), which sends the session in the link.
          </p>
        </div>
      </div>
    )
  }

  if (ssoError) {
    return (
      <div className="app-shell">
        <div className="auth-error card">
          <h2>Could not sign you in</h2>
          <p>{ssoError}</p>
          <p className="text-muted font12">
            The sign-in token was missing, expired or rejected. Opening the record again from the CRM
            issues a fresh one.
          </p>
          <button type="button" className="btn pri-btn" onClick={() => redirectToSsoLogin(target)}>
            Sign in
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="app-shell">
      <div className="auth-error card">
        <h2>You are not signed in</h2>
        <p>
          This link carried no sign-in token, so there is no session to open the record with. Taking
          you to sign-in…
        </p>
        <div className="loader" style={{ margin: '0 auto 16px' }} />
        <button type="button" className="btn pri-btn" onClick={() => redirectToSsoLogin(target)}>
          {redirecting ? 'Redirecting…' : 'Sign in'}
        </button>
      </div>
    </div>
  )
}
